import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CreateUserDTO, User } from '../../model/user';
import { UserService } from 'src/app/admin/services/user.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DividerModule } from 'primeng/divider';
import { TranslocoService, TranslocoModule } from '@jsverse/transloco';
import { ApiError } from 'src/app/trip-calculator/models/api-response';

@Component({
  selector: 'app-user-form-popup',
  imports: [
    ButtonModule,
    InputTextModule,
    SelectModule,
    ReactiveFormsModule,
    DialogModule,
    ConfirmDialogModule,
    DividerModule,
    TranslocoModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './user-form-popup.component.html',
  styleUrl: './user-form-popup.component.scss',
})
export class UserFormPopupComponent implements OnInit, OnChanges {
  visible: boolean = false;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() user?: User;
  @Output() emitUserDTO = new EventEmitter<CreateUserDTO>();
  @Output() emitDelete = new EventEmitter<void>();
  @Input() loading = false;

  fb = inject(FormBuilder);
  userService = inject(UserService);
  confirmationService = inject(ConfirmationService);
  notificationService = inject(NotificationService);
  translocoService = inject(TranslocoService);
  currentUserService = inject(CurrentUserService);
  isResettingPassword = false;

  roleOptions: { label: string; value: string; disabled?: boolean }[] = [];
  isEditingSelf = false;

  userForm = this.fb.group({
    firstName: [''],
    lastName: [''],
    email: ['', [Validators.email, Validators.required]],
    phone: [''],
    role: [''],
  });

  ngOnInit() {
    this.buildRoleOptions();
    if (this.mode === 'edit' && this.user) {
      this.populateForm(this.user);
    }
  }

  buildRoleOptions() {
    this.roleOptions = [];
    const currentUserId = this.currentUserService.currentUser()?.id;
    this.isEditingSelf = this.mode === 'edit' && this.user?.id === currentUserId;

    // If editing own account and user is superAdmin, show disabled option
    if (this.isEditingSelf && this.user?.role === 'superAdmin') {
      this.roleOptions.push({
        label: this.translocoService.translate('accountMenu.usersManagement.roleSuperAdmin'),
        value: 'superAdmin',
        disabled: true,
      });
    }
    // For other users, don't show the superAdmin option

    this.roleOptions.push(
      { label: this.translocoService.translate('accountMenu.usersManagement.roleAdmin'), value: 'admin' },
      { label: this.translocoService.translate('accountMenu.usersManagement.roleUser'), value: 'user' },
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']) {
      const previous = changes['loading'].previousValue;
      const current = changes['loading'].currentValue;
      if (previous === true && current === false) {
        this.visible = false;
      }
    }
  }
  populateForm(user: User) {
    this.userForm.patchValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.emails?.[0]?.email,
      phone: user.phone,
      role: user.role,
    });
  }
  showDialog() {
    this.buildRoleOptions();
    if (this.mode === 'edit' && this.user) {
      this.populateForm(this.user);
    }
    this.visible = true;
  }

  submit() {
    if (this.userForm.valid) {
      const userDTO: CreateUserDTO = {
        ...this.user,
        firstName: this.userForm.value.firstName ?? '',
        lastName: this.userForm.value.lastName ?? '',
        email: this.userForm.value.email!,
        phone: this.userForm.value.phone ?? '',
        role: this.userForm.value.role ?? '',
      };
      this.emitUserDTO.emit(userDTO);
      this.userForm.reset();
    }
  }

  deleteUser() {
    const message = this.translocoService.translate('accountMenu.usersManagement.confirmDelete');
    const header = this.translocoService.translate('accountMenu.usersManagement.deleteConfirmation');
    const acceptLabel = this.translocoService.translate('accountMenu.usersManagement.confirmYes');
    const rejectLabel = this.translocoService.translate('accountMenu.usersManagement.confirmNo');

    this.confirmationService.confirm({
      message,
      header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel,
      rejectLabel,
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.visible = false;
        this.emitDelete.emit();
      },
    });
  }

  triggerPasswordReset() {
    if (!this.user?.id) return;

    const userEmail = this.user.emails?.[0]?.email || this.translocoService.translate('admin.passwordReset.unknownUser');

    const confirmMessageTemplate = this.translocoService.translate('admin.passwordReset.confirmMessage');
    const confirmMessage = confirmMessageTemplate.replace('{userEmail}', userEmail);

    const confirmTitle = this.translocoService.translate('admin.passwordReset.confirmTitle');
    const acceptLabel = this.translocoService.translate('admin.passwordReset.confirmAccept');
    const rejectLabel = this.translocoService.translate('admin.passwordReset.confirmReject');

    this.confirmationService.confirm({
      message: confirmMessage,
      header: confirmTitle,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: acceptLabel,
      rejectLabel: rejectLabel,
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.isResettingPassword = true;
        this.userService.triggerPasswordReset(this.user!.id!).subscribe({
          next: () => {
            this.isResettingPassword = false;
            const successMessageTemplate = this.translocoService.translate('admin.passwordReset.successMessage');
            const successMessage = successMessageTemplate.replace('{userEmail}', userEmail);
            this.notificationService.success(successMessage);
          },
          error: (err: ApiError) => {
            this.isResettingPassword = false;
            const errorMessage = this.translocoService.translate('admin.passwordReset.errorMessage');
            this.notificationService.error(err.error.message || errorMessage);
          },
        });
      },
    });
  }
}
